import type { ReactNode } from 'react';
import { BarChart2, CalendarDays, TrendingUp, ShieldCheck, User, Heart } from 'lucide-react';
import type { StudentProfileDetails } from './profileData';

interface ProfileHeroCardProps {
  details: StudentProfileDetails;
  /**
   * The child's photograph, laid over the initials. Passed only by a panel that
   * can read it; while it loads, or if it fails, the initials show through.
   */
  photo?: ReactNode;
}

/** Their initials, for the badge where a photograph would be. */
function initialsOf(fullName: string): string {
  return fullName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * The card at the top of the profile.
 *
 * Read-only, like the rest of the screen. The badge shows the photograph the
 * office uploaded when the caller passes one, and the child's initials
 * otherwise — never a stand-in face.
 */
export function ProfileHeroCard({ details, photo }: ProfileHeroCardProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 ">
      {/* 1. Left Student Avatar & Bio Card (5 cols) */}
      <div className="lg:col-span-5 rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 px-5 py-3 shadow-xs flex flex-col sm:flex-row items-center gap-5">
        {/* Initials badge */}
        <div className="shrink-0">
          <div
            className="relative h-24 w-24 sm:h-28 sm:w-28 overflow-hidden rounded-full ring-4 ring-indigo-500/20 dark:ring-indigo-400/20 bg-gradient-to-b from-indigo-100 to-indigo-50 dark:from-indigo-950/60 dark:to-slate-900 flex items-center justify-center text-2xl sm:text-3xl font-extrabold text-indigo-700 dark:text-indigo-200"
            aria-hidden
          >
            {initialsOf(details.fullName)}
            {photo && <div className="absolute inset-0">{photo}</div>}
          </div>
        </div>

        {/* Student Details */}
        <div className="flex flex-col items-center sm:items-start text-center sm:text-left min-w-0 flex-1">
          <h2 className="text-lg sm:text-xl font-extrabold text-slate-700 dark:text-white truncate">
            {details.fullName}
          </h2>

          <p className="text-xs sm:text-sm font-semibold text-slate-600 dark:text-slate-300 mt-0.5">
            {details.className} <span className="mx-1">•</span> Roll No. {details.rollNumber}
            {details.house && (
              <>
                <span className="mx-1">•</span> House: {details.house}
              </>
            )}
          </p>

          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-800 mt-2">
            {details.status}
          </span>
        </div>
      </div>

      {/* 2. Right Quick Info KPI 6-Grid Cards (7 cols) */}
      <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
        {/* Card 1: Academic Year */}
        <div className="h-full rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 px-4 py-3 shadow-xs flex items-center gap-3.5 hover:border-sky-300 dark:hover:border-slate-700 transition-colors">
          <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl bg-sky-50 text-sky-600 dark:bg-sky-950/60 dark:text-sky-400 border border-sky-200/70 dark:border-sky-800/60 flex items-center justify-center shrink-0">
            <CalendarDays className="h-5 w-5" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-xs sm:text-base font-semibold text-slate-500 dark:text-slate-400 tracking-wide">
              Academic Year
            </span>
            <span className="text-sm sm:text-lg font-bold text-slate-900 dark:text-white truncate mt-0.5">
              {details.academicYear}
            </span>
          </div>
        </div>

        {/* Card 2: Admission No */}
        <div className="h-full rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 px-4 py-3 shadow-xs flex items-center gap-3.5 hover:border-emerald-300 dark:hover:border-slate-700 transition-colors">
          <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-200/70 dark:border-emerald-800/60 flex items-center justify-center shrink-0">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-xs sm:text-base font-semibold text-slate-500 dark:text-slate-400 tracking-wide">
              Admission No.
            </span>
            <span className="text-sm sm:text-lg font-bold text-slate-900 dark:text-white truncate font-mono mt-0.5">
              {details.admissionNumber}
            </span>
          </div>
        </div>

        {/* Card 3: Gender */}
        <div className="h-full rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 px-4 py-3 shadow-xs flex items-center gap-3.5 hover:border-pink-300 dark:hover:border-slate-700 transition-colors">
          <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl bg-pink-50 text-pink-600 dark:bg-pink-950/60 dark:text-pink-400 border border-pink-200/70 dark:border-pink-800/60 flex items-center justify-center shrink-0">
            <User className="h-5 w-5" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-xs sm:text-base font-semibold text-slate-500 dark:text-slate-400 tracking-wide">
              Gender
            </span>
            <span className="text-sm sm:text-lg font-bold text-slate-900 dark:text-white truncate mt-0.5">
              {details.gender ?? '—'}
            </span>
          </div>
        </div>

        {/* Card 4: Attendance */}
        <div className="h-full rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 px-4 py-3 shadow-xs flex items-center gap-3.5 hover:border-teal-300 dark:hover:border-slate-700 transition-colors">
          <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl bg-teal-50 text-teal-600 dark:bg-teal-950/60 dark:text-teal-400 border border-teal-200/70 dark:border-teal-800/60 flex items-center justify-center shrink-0">
            <BarChart2 className="h-5 w-5" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-xs sm:text-base font-semibold text-slate-500 dark:text-slate-400 tracking-wide">
              Attendance
            </span>
            <span className="text-sm sm:text-lg font-bold text-slate-900 dark:text-white truncate mt-0.5">
              {details.attendance.percentage !== null ? `${details.attendance.percentage}%` : '—'}
            </span>
          </div>
        </div>

        {/* Card 5: Overall Score */}
        <div className="h-full rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 px-4 py-3 shadow-xs flex items-center gap-3.5 hover:border-amber-300 dark:hover:border-slate-700 transition-colors">
          <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl bg-amber-50 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400 border border-amber-200/70 dark:border-amber-800/60 flex items-center justify-center shrink-0">
            <TrendingUp className="h-5 w-5" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-xs sm:text-base font-semibold text-slate-500 dark:text-slate-400 tracking-wide">
              Overall Score
            </span>
            <span className="text-sm sm:text-lg font-bold text-slate-900 dark:text-white truncate mt-0.5">
              {details.overallPerformance !== null ? `${details.overallPerformance.averageScore}%` : '—'}
            </span>
          </div>
        </div>

        {/* Card 6: Blood Group */}
        <div className="h-full rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 px-4 py-3 shadow-xs flex items-center gap-3.5 hover:border-rose-300 dark:hover:border-slate-700 transition-colors">
          <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl bg-rose-50 text-rose-600 dark:bg-rose-950/60 dark:text-rose-400 border border-rose-200/70 dark:border-rose-800/60 flex items-center justify-center shrink-0">
            <Heart className="h-5 w-5 fill-current" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-xs sm:text-base font-semibold text-slate-500 dark:text-slate-400 tracking-wide">
              Blood Group
            </span>
            <span className="text-sm sm:text-lg font-bold text-slate-900 dark:text-white truncate mt-0.5">
              {details.bloodGroup ?? 'Not recorded'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
